import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';

import * as QuoteListActions from './quote-list.actions';
import {
	map,
	tap,
	switchMap,
	withLatestFrom,
	distinctUntilChanged,
	skip,
} from 'rxjs/operators';
import { DataPersistence } from '@nrwl/angular';
import { JobsClient, JobStatuses } from 'src/shared/api';
import { Router, ActivatedRouteSnapshot } from '@angular/router';
import { quotesFragments } from '../../quotes.routes';
import { QuoteListComponent } from '../quote-list.component';
import {
	convertToQuotesParams,
	convertToQuotesListArgs,
} from '../quote-list.utility';
import { UserPreferencesService } from 'src/shared/services/user-preferences.service';
import { isMissingPagingArgs } from 'src/admin-app/utilities/list-filtering/shared';
import { addPagingListArgs } from 'src/shared/utilities/custom-rxjs-operators';
import {
	QuoteListPartialState,
	quoteListFeatureKey,
} from './quote-list.reducer';
import { isEmpty } from 'lodash';
import { Store } from '@ngrx/store';

@Injectable()
export class QuoteListEffects {
	updateListQueryParams$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(QuoteListActions.filtersUpdated),
				map(({ filters }) => convertToQuotesParams(filters)),
				tap((queryParams) => {
					this.router.navigate(quotesFragments.list, {
						queryParams,
					});
				})
			),
		{ dispatch: false }
	);

	updateFilterTeamLocal$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(QuoteListActions.filtersUpdated),
				map(({ filters }) => filters.teamId),
				distinctUntilChanged(),
				skip(1),
				map((filterTeamId) => ({
					updated: this.userPreferences.setFilterTeamIdLocal(
						filterTeamId
					),
					filterTeamId,
				})),
				tap(({ updated, filterTeamId }) =>
					updated
						? this.store.dispatch(
								QuoteListActions.localTeamPrefUpdated({
									teamId: filterTeamId,
								})
						  )
						: null
				)
			),
		{ dispatch: false }
	);

	updateFilterTeamApi$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(QuoteListActions.localTeamPrefUpdated),
				map(({ teamId }) => teamId),
				distinctUntilChanged(),
				tap((filterTeamId) => {
					this.userPreferences.setFilterTeamIdApi(filterTeamId);
				})
			),
		{ dispatch: false }
	);

	updatePageSizeLocal$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(QuoteListActions.filtersUpdated),
				map(({ filters }) => filters.pageSize),
				distinctUntilChanged(),
				skip(1),
				map((pageSize) => ({
					updated: this.userPreferences.setListPageSizeLocal(
						pageSize
					),
					pageSize,
				})),
				tap(({ updated, pageSize }) =>
					updated
						? this.store.dispatch(
								QuoteListActions.localPagePrefUpdated({
									pageSize: pageSize,
								})
						  )
						: null
				)
			),
		{ dispatch: false }
	);

	updatePageSizeApi$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(QuoteListActions.localPagePrefUpdated),
				map(({ pageSize }) => pageSize),
				distinctUntilChanged(),
				tap((pageSize) => {
					this.userPreferences.setListPageSizeApi(pageSize);
				})
			),
		{ dispatch: false }
	);

	addDefaultParams$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteListActions.navigatedWithoutAnyParams),
			withLatestFrom(this.userPreferences.filterTeamId$),
			map(([_, filterTeamId]) =>
				QuoteListActions.navigatedWithoutPagingParams({
					listArgs: { teamId: filterTeamId },
				})
			)
		)
	);

	addPagingListArgs$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteListActions.navigatedWithoutPagingParams),
			map((action) => action.listArgs),
			addPagingListArgs(this.userPreferences),
			map((filters) => QuoteListActions.filtersUpdated({ filters }))
		)
	);

	getQuotes$ = createEffect(() =>
		this.dataPersistence.navigation(QuoteListComponent, {
			run: (r: ActivatedRouteSnapshot, _state: QuoteListPartialState) => {
				const listArgs = convertToQuotesListArgs(r.queryParamMap);

				if (isEmpty(listArgs)) {
					return QuoteListActions.navigatedWithoutAnyParams();
				}

				if (isMissingPagingArgs(listArgs)) {
					return QuoteListActions.navigatedWithoutPagingParams({
						listArgs,
					});
				}

				listArgs.jobStatuses = [
					JobStatuses.CustomerEntry,
					JobStatuses.Submitted,
					JobStatuses.Reviewing,
				];

				return this.jobsClient.getJobs(listArgs).pipe(
					map((pagedQuotes) =>
						QuoteListActions.pagedQuotesLoaded({
							pagedQuotes,
							listArgs,
						})
					)
				);
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	cancelQuote$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			QuoteListActions.userConfirmedCancelQuote,
			{
				run: (
					action: ReturnType<
						typeof QuoteListActions.userConfirmedCancelQuote
					>,
					state: QuoteListPartialState
				) => {
					return this.jobsClient
						.cancelJobOrQuote(action.quoteId)
						.pipe(
							switchMap(() =>
								this.jobsClient
									.getJobs(
										state[quoteListFeatureKey].listArgs
									)
									.pipe(
										map((pagedQuotes) =>
											QuoteListActions.pagedQuotesLoaded({
												pagedQuotes,
												listArgs:
													state[quoteListFeatureKey]
														.listArgs,
											})
										)
									)
							)
						);
				},
				onError: (_a: any, error: any) => {
					throw new Error(error);
				},
			}
		)
	);

	constructor(
		private readonly actions$: Actions,
		private readonly dataPersistence: DataPersistence<
			QuoteListPartialState
		>,
		private readonly userPreferences: UserPreferencesService,
		private readonly jobsClient: JobsClient,
		private readonly store: Store<QuoteListPartialState>,
		private readonly router: Router
	) {}
}
